/**
 * PathwayCompareCard — side-by-side headline metrics (R-131-FIN-DECISION-F /
 * R-131-FIN-DECISION-E WACC / R-131-FIN-DECISION-G carbon overlay).
 *
 * SURFACE-UI additions (R-131-FIN-DECISION-PWIN / -SCENARIO / -PHASE-LCOE):
 *  - mounts PathwayBandChart (P10/P50/P90 LCOE+NPV bands) in place of the
 *    text-only "p10..p90" cell;
 *  - adds a pWin row (correlated-MC probability cheapest / highest-NPV);
 *  - adds phase-level LCOE sub-rows from `metric.phaseBreakdown` (e.g. hybrid
 *    bridge: bridge-phase + grid-phase + blended);
 *  - renders scenario-sensitivity conditional callouts.
 *
 * All additions are ADDITIVE: when the new data/props are absent the original
 * rows render unchanged. No numbers are fabricated — every figure comes from
 * the CORE APIs / existing PathwayCompareMetrics fields.
 */
import React, { useMemo, useState } from 'react';
import { applyPathwayCarbonAdjust } from '../../../lib/finDecision/pathwayCarbonAdjust';
import {
  computeAllPathwayCompareMetrics,
  formatAnnualEmissions,
  formatPct,
  formatRiskPremiumBps,
  formatUsdCompactMillions,
  formatUsdPerMwh,
} from '../../../lib/finDecision/pathwayCompareMetrics';
import { computePathwayLcoe } from '../../../lib/finDecision/pathwayLcoe';
import {
  DEFAULT_COMPARE_MONTE_CARLO_OPTS,
  runComparisonMonteCarlo,
} from '../../../lib/finDecision/pathwayMonteCarlo';
import {
  runScenarioSensitivity,
  type ScenarioResult,
} from '../../../lib/finDecision/scenarioSensitivity';
import {
} from '../../../lib/finDecision/presets';
import {
  formatRiskAdjustedWaccLabel,
  riskAdjustedWaccFromPathway,
} from '../../../lib/finDecision/riskPremium';
import type { Pathway } from '../../../lib/finDecision/types';
import type { PathwayHeadlineTone } from '../../../lib/finDecision/pathwayCompareMetrics';
import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import { PathwayBandChart, type PathwayBandDatum } from './PathwayBandChart';

/**
 * Headline-tone → Aurora band-glow variable. The headline row is a DECISION
 * signal ("🟢 Fastest" / "🔴 Long lock" / "🟡 Mid-term"), so its dot carries the
 * shared `.glow-band` bloom (gated to decision tones) — the same glow the
 * portfolio VerdictDot uses. Returns the glow colour var for the `.glow-band`
 * recipe (see verdictVisuals.bandGlowColorVar / index.css). The glow tokens are
 * zeroed inside the report island, so this renders flat there.
 */
function headlineToneGlowVar(tone: PathwayHeadlineTone): string {
  switch (tone) {
    case 'positive':
      return 'var(--success)';
    case 'negative':
      return 'var(--danger)';
    case 'neutral':
    default:
      return 'var(--warning)';
  }
}

/**
 * Strip a leading status emoji (🔴 / 🟢 / 🟡, incl. variation selectors) from a
 * headline label. The Aurora glow dot now carries the decision tone, so the
 * emoji would render as a DUPLICATE coloured dot beside it.
 */
function stripHeadlineEmoji(label: string): string {
  return label.replace(/^[\p{Extended_Pictographic}️‍\s]+/u, '');
}

export interface PathwayCompareCardProps {
  pathways: Pathway[];
  carbonCommitment?: ProjectIntent['carbonCommitment'];
  carbonPricePerTUsd?: number;
  carbonOverlayEnabled?: boolean;
  onCarbonOverlayEnabledChange?: (enabled: boolean) => void;
  /**
   * Optional analysis context, carried for the metrics below.
   *
   * Q7 — these used to gate a BRIDGE-DURATION SLIDER that rebuilt a
   * hybrid-bridge pathway on every drag. That preset was retired: the cashflow
   * engine cannot represent a sequential bridge (it dates COD from the first
   * construction start, reads energy from only the first operation phase, and
   * charges both legs' opex in every year), so the slider swept a curve the
   * engine had fabricated and reported a "crossover year" derived from it.
   */
  intent?: ProjectIntent;
  siteContext?: SiteContext;
  /** Adopt recomputed pathways. */
  onPathwaysChange?: (pathways: Pathway[]) => void;
  /** Correlated-MC iterations for the pWin row (default 400, the UI MC path). */
  pWinRuns?: number;
  /** Seed for the pWin correlated MC (default 1, deterministic). */
  pWinSeed?: number;
}

const CARBON_OVERLAY_OPTIONS = [
  { value: true, label: 'With carbon price' },
  { value: false, label: 'No carbon' },
] as const;

type CompareRowKey =
  | 'lcoe'
  | 'npv'
  | 'irr'
  | 'pWin'
  | 'timeToPower'
  | 'lifetime'
  | 'carbon'
  | 'riskPremium'
  | 'headline';

const ROW_LABELS: Record<CompareRowKey, string> = {
  lcoe: 'LCOE (real)',
  npv: 'NPV @WACC',
  irr: 'IRR',
  pWin: 'pWin (correlated MC)',
  timeToPower: 'Time to power',
  lifetime: 'Lifetime',
  carbon: 'Carbon (tCO₂/y)',
  riskPremium: 'Risk premium',
  headline: 'Headline',
};


/**
 * Compact, NON-COLLIDING column label for the band chart.
 *
 * Two requirements drive this:
 *   1. PHASE DISTINGUISHER — a phased intent emits one pathway per phase, often
 *      sharing a technology (e.g. "Phase 1 Grid import" + "Phase 3 Grid import").
 *      Collapsing to a bare "Grid 2029" made two distinct phases IDENTICAL on the
 *      surface. We therefore preserve the leading "Phase N" token when present.
 *   2. MONTH-RESOLUTION COD — the pathway name now carries a month-precision COD
 *      ("Jun 2029") when the user typed an exact in-service date. We keep that
 *      whole token (month + year, or year-only when only a year was entered) so
 *      same-year milestones stay distinguishable. We do NOT fabricate a month.
 *
 * The full pathway name remains the authoritative label (shown in the tooltip /
 * SR table); this only abbreviates the verbose TECHNOLOGY phrase in the middle
 * to keep the column header compact, never the phase prefix or the COD suffix.
 */
function shortColumnLabel(pathway: Pathway): string {
  // COD token: a month-resolution "MMM YYYY" (e.g. "Jun 2029") when present,
  // else a bare year. Captured straight off the name so we never drop the month.
  const codMatch = pathway.name.match(/\b([A-Z][a-z]{2}\s)?(20\d{2})\b/);
  const cod = codMatch ? `${codMatch[1] ?? ''}${codMatch[2]}`.trim() : '';
  // Phase distinguisher ("Phase 1", "Phase 2", …) — kept so same-tech phases
  // never collapse to one indistinguishable column.
  const phaseMatch = pathway.name.match(/^(Phase\s+\d+)/i);
  const phasePrefix = phaseMatch ? `${phaseMatch[1]} ` : '';

  const techShort = ((): string => {
    switch (pathway.technology) {
      case 'gas-cc':
        return 'Gas-CC';
      case 'nuclear-smr':
        return 'SMR';
      case 'grid-tariff':
        return 'Grid';
      case 'mixed':
        return 'Hybrid';
      default: {
        // Strip the phase prefix + trailing COD off the full name to recover the
        // technology phrase, then keep it whole (no lossy mid-word truncation —
        // the user must be able to read the full leg, e.g. "Reciprocating
        // Engine"). Wrapping to two lines is handled by the renderer.
        let body = pathway.name;
        if (phaseMatch) body = body.slice(phaseMatch[1].length);
        if (codMatch) body = body.replace(codMatch[0], '');
        body = body.trim();
        return body || pathway.name;
      }
    }
  })();

  return `${phasePrefix}${techShort}${cod ? ` ${cod}` : ''}`.trim();
}

function formatLcoeAttribution(usdPerMwh: number): string {
  if (usdPerMwh <= 0) return '—';
  return `$${usdPerMwh.toFixed(1)}/MWh LCOE`;
}

function formatPWin(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—';
  return `${Math.round(fraction * 100)}%`;
}


export function PathwayCompareCard({
  pathways,
  carbonCommitment,
  carbonPricePerTUsd,
  carbonOverlayEnabled,
  onCarbonOverlayEnabledChange,
  intent,
  siteContext,
  onPathwaysChange,
  pWinRuns,
  pWinSeed,
}: PathwayCompareCardProps) {
  const [internalCarbonOverlayEnabled, setInternalCarbonOverlayEnabled] = useState(true);
  const overlayEnabled = carbonOverlayEnabled ?? internalCarbonOverlayEnabled;

  const setOverlayEnabled = (enabled: boolean) => {
    if (carbonOverlayEnabled === undefined) {
      setInternalCarbonOverlayEnabled(enabled);
    }
    onCarbonOverlayEnabledChange?.(enabled);
  };

  const metrics = useMemo(
    () =>
      computeAllPathwayCompareMetrics(pathways, {
        carbonCommitment,
        carbonPricePerTUsd,
      }),
    [pathways, carbonCommitment, carbonPricePerTUsd],
  );

  // R-131-FIN-DECISION-PWIN — correlated-scenario win probabilities. Only run
  // when there is a contest (≥2 pathways); a single pathway has no win/lose.
  const pWinByPathway = useMemo(() => {
    if (pathways.length < 2) return null;
    const result = runComparisonMonteCarlo(pathways, {
      ...DEFAULT_COMPARE_MONTE_CARLO_OPTS,
      runs: pWinRuns ?? 400,
      seedRng: pWinSeed ?? 1,
    });
    const byId = new Map<string, { pWinLcoe: number; pWinNpv: number }>();
    result.pathways.forEach((p) =>
      byId.set(p.pathwayId, { pWinLcoe: p.pWinLcoe, pWinNpv: p.pWinNpv }),
    );
    return byId;
  }, [pathways, pWinRuns, pWinSeed]);

  // R-131-FIN-DECISION-SCENARIO — conditional callouts ("Recommend X if low
  // fuel; Y if long queue"). Deterministic; only meaningful with ≥2 pathways.
  const scenarios = useMemo<ScenarioResult[]>(() => {
    if (pathways.length < 2) return [];
    // Carbon pins use the runner's documented SCENARIO_LOW/HIGH_CARBON_PRICE
    // constants; the headline carbonPricePerTUsd (the median overlay price) is
    // not a scenario tail, so it is intentionally not forwarded here.
    return runScenarioSensitivity(pathways).scenarios.filter((s) => s.applicable);
  }, [pathways]);

  const waccRows = useMemo(
    () =>
      pathways.map((pathway) => {
        const result = riskAdjustedWaccFromPathway(pathway);
        return {
          id: pathway.id,
          label: formatRiskAdjustedWaccLabel(
            shortColumnLabel(pathway),
            pathway.risk.baseWaccPct,
            result,
          ),
          waccPct: result.waccPct,
        };
      }),
    [pathways],
  );

  const carbonRows = useMemo(
    () =>
      pathways.map((pathway) =>
        applyPathwayCarbonAdjust(pathway, {
          carbonCommitment,
          carbonPricePerTUsd,
          applyCarbonOverlay: overlayEnabled,
        }),
      ),
    [pathways, carbonCommitment, carbonPricePerTUsd, overlayEnabled],
  );

  // Band-chart data — sourced entirely from the existing metrics fields.
  const bandData = useMemo<PathwayBandDatum[]>(
    () =>
      metrics.map((row, index) => ({
        pathwayId: row.pathwayId,
        label: shortColumnLabel(pathways[index]),
        classification: pathways[index].classification,
        lcoeRealUsdPerMwh: row.lcoeRealUsdPerMwh,
        lcoeP10: row.lcoeP10,
        lcoeP90: row.lcoeP90,
        npvUsd: row.npvUsd,
        npvP10: row.npvP10,
        npvP90: row.npvP90,
      })),
    [metrics, pathways],
  );

  const cellValues = useMemo(() => {
    const byKey: Record<CompareRowKey, string[]> = {
      lcoe: [],
      npv: [],
      irr: [],
      pWin: [],
      timeToPower: [],
      lifetime: [],
      carbon: [],
      riskPremium: [],
      headline: [],
    };

    metrics.forEach((row, index) => {
      byKey.lcoe.push(formatUsdPerMwh(row.lcoeRealUsdPerMwh));
      byKey.npv.push(formatUsdCompactMillions(row.npvUsd));
      byKey.irr.push(formatPct(row.irrPct));
      const pw = pWinByPathway?.get(row.pathwayId);
      byKey.pWin.push(
        pw ? `${formatPWin(pw.pWinLcoe)} / ${formatPWin(pw.pWinNpv)}` : '—',
      );
      byKey.timeToPower.push(`${row.timeToPowerYears} yr`);
      byKey.lifetime.push(row.lifetimeLabel);
      byKey.carbon.push(
        row.pathwayId && pathways[index]?.technology === 'grid-tariff'
          ? 'varies'
          : formatAnnualEmissions(row.annualTCo2e),
      );
      byKey.riskPremium.push(formatRiskPremiumBps(row.riskPremiumBps));
      byKey.headline.push(row.headline.label);
    });

    return byKey;
  }, [metrics, pathways, pWinByPathway]);

  // Phase-level LCOE sub-rows. Only multi-segment pathways (hybrid bridge) carry
  // a >1-element breakdown; single-phase pathways carry one element spanning the
  // whole lifecycle (no sub-rows worth showing). We surface sub-rows when ANY
  // pathway has a multi-element breakdown.
  const hasPhaseSubRows = useMemo(
    () => metrics.some((m) => m.phaseBreakdown.length > 1),
    [metrics],
  );


  if (pathways.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface-1 p-4 text-sm text-text-secondary">
        Select pathways to compare.
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-border bg-surface-1 p-4"
      data-testid="pathway-compare-card"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">Pathway comparison</h3>
        <div
          className="flex items-center gap-2"
          data-testid="pathway-carbon-overlay-toggle"
        >
          {CARBON_OVERLAY_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => setOverlayEnabled(opt.value)}
              className={`rounded-md px-2.5 py-1 type-meta border ${
                overlayEnabled === opt.value
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-border text-text-secondary hover:bg-state-hover'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* P10/P50/P90 confidence bands — replaces the old text-only band cell. */}
      <div className="mb-4">
        <PathwayBandChart data={bandData} />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-secondary">
              <th className="px-2 py-1 font-medium w-36" />
              {pathways.map((pathway) => (
                <th key={pathway.id} className="px-2 py-1 font-medium text-text-primary">
                  {shortColumnLabel(pathway)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(Object.keys(ROW_LABELS) as CompareRowKey[]).map((key) => {
              // Hide the pWin row entirely for a single-pathway comparison.
              if (key === 'pWin' && !pWinByPathway) return null;
              return (
                <React.Fragment key={key}>
                  <tr className="border-b border-border/60 last:border-0">
                    <td className="px-2 py-2 text-text-secondary font-medium">{ROW_LABELS[key]}</td>
                    {pathways.map((pathway, index) => (
                      <td
                        key={`${pathway.id}-${key}`}
                        className="px-2 py-2 text-text-primary tabular-nums"
                        data-testid={
                          key === 'riskPremium'
                            ? `pathway-wacc-detail-${pathway.id}`
                            : key === 'carbon'
                              ? `pathway-carbon-${pathway.id}`
                              : key === 'lcoe'
                                ? `pathway-lcoe-${pathway.id}`
                                : key === 'pWin'
                                  ? `pathway-pwin-${pathway.id}`
                                  : key === 'headline'
                                    ? `pathway-headline-${pathway.id}`
                                    : undefined
                        }
                      >
                        {key === 'headline' ? (
                          <span className="inline-flex items-center gap-1.5">
                            {/* Aurora decision-band glow dot — blooms its band
                                colour via the shared `.glow-band` recipe, gated
                                to the headline decision tones. The dot now
                                carries the tone, so the label's leading status
                                emoji is stripped to avoid a DUPLICATE dot. */}
                            <span
                              className="inline-block w-2 h-2 rounded-full shrink-0 glow-band"
                              style={
                                {
                                  backgroundColor: headlineToneGlowVar(metrics[index].headline.tone),
                                  '--glow-band-color': headlineToneGlowVar(metrics[index].headline.tone),
                                } as React.CSSProperties
                              }
                              aria-hidden="true"
                              data-testid={`pathway-headline-dot-${pathway.id}`}
                            />
                            <span>{stripHeadlineEmoji(String(cellValues[key][index]))}</span>
                          </span>
                        ) : (
                          cellValues[key][index]
                        )}
                        {key === 'pWin' ? (
                          <div className="text-[10px] text-text-muted mt-0.5">cheapest / best-NPV</div>
                        ) : null}
                        {key === 'carbon' && overlayEnabled ? (
                          <div className="text-[10px] text-text-muted mt-0.5">
                            {formatLcoeAttribution(carbonRows[index]?.lcoeCarbonAttributionUsdPerMwh ?? 0)}
                          </div>
                        ) : null}
                        {key === 'riskPremium' ? (
                          <div className="text-[10px] text-text-muted mt-0.5">
                            {waccRows[index]?.label}
                          </div>
                        ) : null}
                      </td>
                    ))}
                  </tr>

                  {/* Phase-level LCOE sub-rows directly under the LCOE row. */}
                  {key === 'lcoe' && hasPhaseSubRows
                    ? renderPhaseSubRows(metrics)
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Scenario-sensitivity conditional callouts. */}
      {scenarios.length > 0 ? (
        <div className="mt-4" data-testid="pathway-scenario-callouts">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary mb-1.5">
            Scenario sensitivity
          </div>
          <ul className="space-y-1">
            {scenarios.map((s) => (
              <li
                key={s.scenarioId}
                className="text-xs text-text-secondary"
                data-testid={`pathway-scenario-${s.scenarioId}`}
              >
                {formatScenarioCallout(s)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

    </div>
  );
}

/** Format one scenario into a conditional recommendation line. */
function formatScenarioCallout(s: ScenarioResult): string {
  const byLcoe = s.winnerByLcoe?.name ?? '—';
  const byNpv = s.winnerByNpv?.name ?? '—';
  if (byLcoe === byNpv) {
    return `${s.label}: ${byLcoe} leads (LCOE & NPV).`;
  }
  return `${s.label}: ${byLcoe} cheapest, ${byNpv} best NPV.`;
}

/**
 * Render per-phase LCOE attribution sub-rows beneath the LCOE row. Each phase
 * segment becomes a sub-row showing its attributed real LCOE per pathway. The
 * energy-PV-weighted blend of these reconciles to the headline LCOE (asserted
 * in the lib tests). Pathways with a single-element breakdown show "—" in the
 * sub-rows (their whole-pathway LCOE already appears in the LCOE row).
 */
function renderPhaseSubRows(
  metrics: ReturnType<typeof computeAllPathwayCompareMetrics>,
): React.ReactNode {
  const maxPhases = metrics.reduce((max, m) => Math.max(max, m.phaseBreakdown.length), 0);
  if (maxPhases <= 1) return null;

  const rows: React.ReactNode[] = [];
  for (let phaseIdx = 0; phaseIdx < maxPhases; phaseIdx += 1) {
    // Use the first multi-phase pathway's label for the sub-row caption.
    const labelSource = metrics.find((m) => m.phaseBreakdown.length > phaseIdx);
    const phaseLabel = labelSource?.phaseBreakdown[phaseIdx]?.label ?? `Phase ${phaseIdx + 1}`;
    rows.push(
      <tr
        key={`phase-${phaseIdx}`}
        className="border-b border-border/40 bg-surface-0/40"
        data-testid={`pathway-phase-lcoe-row-${phaseIdx}`}
      >
        <td className="px-2 py-1 pl-5 text-[11px] text-text-muted italic">↳ {phaseLabel}</td>
        {metrics.map((m) => {
          const seg = m.phaseBreakdown[phaseIdx];
          return (
            <td
              key={`${m.pathwayId}-phase-${phaseIdx}`}
              className="px-2 py-1 text-[11px] text-text-secondary tabular-nums"
              data-testid={`pathway-phase-lcoe-${m.pathwayId}-${phaseIdx}`}
            >
              {seg && Number.isFinite(seg.lcoeRealUsdPerMwh)
                ? formatUsdPerMwh(seg.lcoeRealUsdPerMwh)
                : '—'}
            </td>
          );
        })}
      </tr>,
    );
  }
  return rows;
}
